import { z } from 'zod';
import type { TaskScheduleToken } from '@sleed/bizcommerce-api';

/**
 * Task schedule edit form. Mirrors `TaskScheduleToken` (Core/client/admin/src/pages/System/
 * TaskSchedules/index.js): nm required, ri (run interval) required. en = enabled, lv = log
 * verbosity, emr = error email recipients. The last-run timestamps (lrsd_l/lred_l/lsd_l) and
 * `run` flag are server-managed and carried through.
 */
export const taskScheduleSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	ri: z.number({ message: 'validation.required' }).int(),
	en: z.boolean(),
	lv: z.number().nullable(),
	emr: z.string().nullable(),
	// Server-managed, carried through:
	run: z.boolean().nullable(),
	lrsd_l: z.string().nullable(),
	lred_l: z.string().nullable(),
	lsd_l: z.string().nullable(),
	fstm: z.string().nullable()
});

export type TaskScheduleFormValues = z.infer<typeof taskScheduleSchema>;

export const EMPTY_TASK_SCHEDULE: TaskScheduleFormValues = {
	nm: '',
	ri: 0,
	en: true,
	lv: null,
	emr: null,
	run: false,
	lrsd_l: null,
	lred_l: null,
	lsd_l: null,
	fstm: null
};

export function tokenToForm(token: TaskScheduleToken): TaskScheduleFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		ri: token.ri ?? 0,
		en: token.en ?? false,
		lv: token.lv ?? null,
		emr: token.emr ?? null,
		run: token.run ?? false,
		lrsd_l: token.lrsd_l ? String(token.lrsd_l) : null,
		lred_l: token.lred_l ? String(token.lred_l) : null,
		lsd_l: token.lsd_l ? String(token.lsd_l) : null,
		fstm: token.fstm ? String(token.fstm) : null
	};
}

export function formToToken(form: TaskScheduleFormValues): TaskScheduleToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		ri: form.ri,
		en: form.en,
		lv: form.lv ?? undefined,
		emr: form.emr,
		run: form.run ?? undefined
	};
}
